import React from 'react';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { MessageTemplateEditor } from '@shared/components/messageTemplateField';
import { POST_CALL_TEMPLATE_PLACEHOLDERS } from '@shared/utils/messageTemplate';
import { useTranslation } from 'react-i18next';

import { AnalysisField } from '@features/Admin/Campaign/utils/campaign-analysis-fields';
import {
  PostCallMessageConditionDraft,
  PostCallMessageDraft,
  PostCallMessageValidationErrors,
} from '@features/Admin/Campaign/utils/post-call-message-mapper';

import PostCallMessageConditionsEditor from './PostCallMessageConditionsEditor';

interface PostCallMessagePopupProps {
  open: boolean;
  draft: PostCallMessageDraft | null;
  errors: PostCallMessageValidationErrors;
  analysisFields: AnalysisField[];
  campaignName: string;
  companyName: string;
  disabled: boolean;
  isLoading: boolean;
  onChange: <K extends keyof PostCallMessageDraft>(key: K, value: PostCallMessageDraft[K]) => void;
  onAddCondition: () => void;
  onRemoveCondition: (index: number) => void;
  onConditionChange: (index: number, condition: PostCallMessageConditionDraft) => void;
  onClose: () => void;
  onSave: () => void;
}

const PostCallMessagePopup: React.FC<PostCallMessagePopupProps> = ({
  open,
  draft,
  errors,
  analysisFields,
  campaignName,
  companyName,
  disabled,
  isLoading,
  onChange,
  onAddCondition,
  onRemoveCondition,
  onConditionChange,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();

  if (!draft) {
    return null;
  }

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <span>
            {draft.id
              ? t('campaignPage.postCallMessages.editTitle')
              : t('campaignPage.postCallMessages.createTitle')}
          </span>
          <FormGroup>
            <FormControlLabel
              sx={{ mr: 0, gap: '8px' }}
              labelPlacement="start"
              control={
                <CustomSwitch
                  checked={draft.isEnabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onChange('isEnabled', event.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  {t('campaignPage.postCallMessages.enabledLabel')}
                </Typography>
              }
            />
          </FormGroup>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <MessageTemplateEditor
          id="post-call-message-template"
          label={t('campaignPage.postCallMessages.templateLabel')}
          value={draft.template}
          onChange={(value) => onChange('template', value)}
          error={errors.template ? t(errors.template) : undefined}
          disabled={disabled}
          previewValues={{ campaignName, companyName }}
          placeholders={POST_CALL_TEMPLATE_PLACEHOLDERS}
          analysisFields={analysisFields.map((field) => field.name)}
          allowDynamicInput={false}
        />

        <PostCallMessageConditionsEditor
          conditions={draft.conditions}
          analysisFields={analysisFields}
          error={errors.conditions ? t(errors.conditions) : undefined}
          disabled={disabled}
          onAdd={onAddCondition}
          onRemove={onRemoveCondition}
          onChange={onConditionChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={onSave} disabled={disabled || isLoading}>
          {isLoading ? <CircularProgress size={20} color="inherit" /> : t('action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostCallMessagePopup;
